<template>
  <div class="fab-container">
    <!-- Back to Top button (always on left side) -->
    <v-btn 
      v-show="showFab" 
      fab 
      small
      color="primary" 
      class="fab-back-to-top" 
      @click="scrollToTop"
      elevation="4"
    >
      <v-icon>mdi-arrow-up</v-icon>
      
      <!-- Back to Top tooltip -->
      <div class="fab-tooltip fab-tooltip-left">
        Back to Top
      </div>
    </v-btn>

    <!-- Main FAB button (right side) -->
    <v-btn 
      v-show="showFab" 
      fab 
      color="primary" 
      class="fab-main" 
      :style="{ bottom, right }" 
      @click="toggleFabMenu"
      elevation="8"
    >
      <v-icon v-if="!fabMenuOpen">{{ mainIcon }}</v-icon>
      <v-icon v-else>mdi-close</v-icon>
    </v-btn>

    <!-- FAB menu items (right side, excluding Back to Top) -->
    <transition-group name="fab-actions">
      <v-btn 
        v-for="(action, index) in actionButtons" 
        v-show="showFab && fabMenuOpen" 
        :key="action.name" 
        fab 
        small
        :color="action.color" 
        class="fab-action" 
        :style="getActionStyle(index)" 
        @click="handleAction(action.action)"
        elevation="4"
      >
        <v-icon>{{ action.icon }}</v-icon>
        
        <!-- Tooltip for each action -->
        <div class="fab-tooltip" :style="getTooltipStyle(index)">
          {{ action.name }}
        </div>
      </v-btn>
    </transition-group>
  </div>
</template>

<script>
export default {
  data() {
    return {
      showFab: false,
      fabMenuOpen: false,
      mainIcon: 'mdi-dots-vertical',
      fabActions: [
        {
          name: 'Back to Top',
          icon: 'mdi-arrow-up',
          color: 'primary',
          action: 'scrollToTop'
        },
        {
          name: 'Download CV',
          icon: 'mdi-download',
          color: 'warning',
          action: 'download'
        },
        {
          name: 'Contact Me',
          icon: 'mdi-email-outline',
          color: 'info',
          action: 'contact'
        },
        {
          name: 'Share',
          icon: 'mdi-share-variant',
          color: 'success',
          action: 'share'
        }
      ]
    };
  },
  props: {
    bottom: {
      type: String,
      default: '65px',
    },
    right: {
      type: String,
      default: '16px',
    }
  },
  computed: {
    // Filter out the Back to Top button for the menu items
    actionButtons() {
      return this.fabActions.filter(action => action.action !== 'scrollToTop');
    }
  },
  mounted() {
    // Scroll event listener to toggle FAB visibility
    window.addEventListener('scroll', this.handleScroll);

    // Close FAB menu when clicking outside
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeDestroy() {
    // Clean up event listeners
    window.removeEventListener('scroll', this.handleScroll);
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
      this.closeFabMenu();
    },

    handleScroll() {
      const scrollPosition = window.scrollY;
      const pageHeight = document.documentElement.scrollHeight - window.innerHeight;
      // Show FAB if scrolled more than 15% down
      this.showFab = scrollPosition > pageHeight * 0.15;

      // Close FAB menu when scrolling
      if (this.fabMenuOpen && scrollPosition > 100) {
        this.closeFabMenu();
      }
    },

    toggleFabMenu() {
      this.fabMenuOpen = !this.fabMenuOpen;
    },

    closeFabMenu() {
      this.fabMenuOpen = false;
    },

    handleOutsideClick(event) {
      const fabContainer = document.querySelector('.fab-container');
      if (this.fabMenuOpen && fabContainer && !fabContainer.contains(event.target)) {
        this.closeFabMenu();
      }
    },

    getActionStyle(index) {
      // Calculate position to ensure no overlap with main FAB
      // We'll position buttons vertically above the main FAB
      const gap = 20; // Gap between buttons
      const buttonHeight = 40; // Small FAB button height
      const mainButtonHeight = 56; // Main FAB button height
      
      // Position starts above the main button with safe gap
      const yOffset = (index + 1) * (buttonHeight + gap) + mainButtonHeight/2;
      
      return {
        bottom: `calc(${this.bottom} + ${yOffset}px)`,
        right: this.right,
        transitionDelay: `${index * 0.05}s`,
        zIndex: 998 - index,
      };
    },

    getTooltipStyle(index) {
      // Match tooltip position to the button
      return {
        right: '60px', // Position to the left of the button
        transform: 'translateY(-50%)', // Center vertically
        transitionDelay: `${index * 0.05 + 0.1}s`
      };
    },

    handleAction(action) {
      switch (action) {
        case 'scrollToTop':
          this.scrollToTop();
          break;
        case 'contact':
          this.$store.commit('setSnackbar', {
            show: true,
            text: 'Opening contact form...',
            color: 'info'
          });
          // Emit event to parent component to show contact form
          this.$root.$emit('show-contact');
          this.closeFabMenu();
          break;
        case 'share':
          this.sharePortfolio();
          break;
        case 'download':
          this.$store.commit('setSnackbar', {
            show: true,
            text: 'CV download started!',
            color: 'success'
          });
          this.closeFabMenu();
          break;
      }
    },

    sharePortfolio() {
      // Check if Web Share API is available
      if (navigator.share) {
        navigator.share({
          title: 'Thomas Antwi - Software Engineer',
          text: 'Check out my portfolio!',
          url: window.location.href,
        })
          .then(() => {
            this.$store.commit('setSnackbar', {
              show: true,
              text: 'Thanks for sharing!',
              color: 'success'
            });
          })
          .catch((error) => {
            console.error('Error sharing:', error);
          });
      } else {
        // Fallback for browsers that don't support Web Share API
        this.copyToClipboard(window.location.href);
        this.$store.commit('setSnackbar', {
          show: true,
          text: 'Portfolio URL copied to clipboard!',
          color: 'success'
        });
      }
      this.closeFabMenu();
    },

    copyToClipboard(text) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
    }
  },
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

.fab-container {
  position: fixed;
  z-index: 998;
  pointer-events: none; /* Allow clicks to pass through container */
}

/* Common styles for all FAB buttons */
.fab-main,
.fab-action,
.fab-back-to-top {
  pointer-events: auto; /* Make buttons clickable */
  box-shadow: 0 6px 10px rgba(0, 0, 0, 0.25);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  
  &:hover {
    transform: scale(1.08);
    box-shadow: 0 8px 15px rgba(0, 0, 0, 0.3);
  }
}

/* Main FAB button */
.fab-main {
  position: fixed;
  z-index: 999;
  backdrop-filter: blur(4px);
  
  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    border-radius: 50%;
    background: linear-gradient(45deg, #6f42c1, #007bff);
    z-index: -1;
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::before {
    opacity: 0.6;
  }
}

/* Back to Top button - always on left side */
.fab-back-to-top {
  position: fixed;
  bottom: 65px;
  left: 16px;
  z-index: 997;
  transition: all 0.3s ease;
  
  /* Glow effect on hover */
  &:hover {
    box-shadow: 0 0 15px rgba(33, 150, 243, 0.5);
  }
  
  .fab-tooltip {
    position: absolute;
    left: 50px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 999;
  }
}

/* Action buttons */
.fab-action {
  position: fixed;
  z-index: 998;
  opacity: 0;
  transform: scale(0) rotate(-90deg);
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  
  &:hover {
    transform: scale(1.08) !important;
    z-index: 999;
  }
}

/* Tooltips */
.fab-tooltip {
  position: absolute;
  z-index: 997;
  background-color: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  opacity: 0;
  pointer-events: none;
  transition: all 0.3s ease;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    right: -5px;
    transform: translateY(-50%);
    border-width: 5px 0 5px 5px;
    border-style: solid;
    border-color: transparent transparent transparent rgba(0, 0, 0, 0.8);
  }
  
  &.fab-tooltip-left::before {
    right: auto;
    left: -5px;
    border-width: 5px 5px 5px 0;
    border-color: transparent rgba(0, 0, 0, 0.8) transparent transparent;
  }
}

/* Show tooltips on hover */
.fab-main:hover + .fab-tooltip,
.fab-action:hover .fab-tooltip,
.fab-back-to-top:hover .fab-tooltip {
  opacity: 1;
}

/* Transitions */
.fab-actions-enter-active {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.fab-actions-leave-active {
  transition: all 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.fab-actions-enter,
.fab-actions-leave-to {
  opacity: 0;
  transform: scale(0) rotate(-90deg);
}

/* Show action buttons when menu is open */
.fab-container:hover .fab-action,
.fab-action.active {
  opacity: 1;
  transform: scale(1) rotate(0);
}

/* Modern glass effect for all buttons */
.fab-main,
.fab-action,
.fab-back-to-top {
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(255,255,255,0.2) 0%, rgba(255,255,255,0) 50%);
    pointer-events: none;
  }
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .fab-main {
    transform: scale(0.9);
    
    &:hover {
      transform: scale(0.98);
    }
  }
  
  .fab-action,
  .fab-back-to-top {
    transform: scale(0.8);
    
    &:hover {
      transform: scale(0.9) !important;
    }
  }
  
  .fab-tooltip {
    font-size: 10px;
    padding: 4px 8px;
  }
}
</style>